import { Component, computed, effect, inject, signal } from "@angular/core"
import { httpResource } from "@angular/common/http"
import { Router } from "@angular/router"
import { ToastService } from "src/app/services"
import { Column, PagedLocation, TableLazyLoadEvent } from "../../models"
import { LocationsService } from "../../services"
import { toPagedParams } from "../../helpers/TablePaging"
import { TableComponent } from "../../components/table/table.component"

@Component({
    selector: "valben-locations-table",
    templateUrl: "./locations-table.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [TableComponent],
})
export class LocationsTablePage {
    private _locationsService = inject(LocationsService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)

    cols: Column[] = [
        { field: "names", subField: "hr", header: "Naslov", bodyType: "langs", filterType: "text", filterMode: "contains" },
        { field: "createdByUser", header: "Kreiranje", bodyType: "text", filterType: "text", filterMode: "contains", pairWith: "modifiedByUser" },
        { field: "modifiedByUser", header: "Zadnja izmjena", bodyType: "text", filterType: "text", filterMode: "contains", prefix: "↳" },
        { field: "createdDate", header: "Datum kreiranja", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true, pairWith: "modifiedDate" },
        { field: "modifiedDate", header: "Datum izmjene", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true },
        { field: "isActive", header: "Aktivan", bodyType: "boolean", filterType: "boolean", sortable: true, trueText: "Aktivna", falseText: "Neaktivna", pairWith: "isDraft" },
        { field: "isDraft", header: "Skica", bodyType: "boolean", filterType: "boolean", sortable: true, trueText: "Skica", falseText: "Bez skice" },
        { field: "version", header: "Verzija", bodyType: "number", filterType: "numeric", sortable: true, prefix: "v" },
        { field: "geolocation", header: "Geolokacija", bodyType: "map", icon: "place" },
    ]

    private _pageRequest = signal<TableLazyLoadEvent | undefined>(undefined)

    locations = httpResource<PagedLocation>(() => {
        const event = this._pageRequest()
        return event ? this._locationsService.PagedRequest(toPagedParams(event, this.cols)) : undefined
    })

    tableValues = computed(() =>
        (this.locations.value()?.items ?? []).map(location => ({
            ...location,
            createdDate: new Date(location.createdDate),
            modifiedDate: new Date(location.modifiedDate),
        }))
    )

    totalRecords = computed(() => this.locations.value()?.totalCount ?? 0)
    loading = computed(() => this.locations.isLoading())

    refresh = () => this.locations.reload()

    constructor() {
        effect(() => {
            if (!this.locations.error()) return
            this._messageService.add({
                severity: "error",
                summary: "Greška",
                detail: "Došlo je do pogreške prilikom dohvata lokacija.",
                life: 10000,
            })
        })
    }

    loadPage(event: TableLazyLoadEvent): void {
        setTimeout(() => this._pageRequest.set(event))
    }

    add(): void {
        this._router.navigate(["/locations/add"])
    }

    edit(masterId: string): void {
        this._router.navigate(["/locations/edit", masterId])
    }
}
